// Location scope — owner's "view the whole app filtered to this location" mode.
// When set, we surface a banner in AppShell and inject `location_id` into all
// GET requests via an axios interceptor. Endpoints that don't understand the
// param silently ignore it; endpoints that do (shifts, users, teams, dashboard)
// filter to just this location.
//
// Feb 2026: switching scope also dispatches a `rostr:location-changed` event
// so any listener outside React can react (e.g. axios cache invalidation),
// and AppShell keys the <Outlet> on the scope so the current page remounts
// with a fresh data-fetch when the user switches.
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const KEY = "rostr.active_location";
const KEY_NAME = "rostr.active_location_name";

const LocationScopeContext = createContext({ id: null, name: null, setScope: () => {}, clear: () => {} });

function emitChange(nextId, nextName) {
  try {
    window.dispatchEvent(new CustomEvent("rostr:location-changed", {
      detail: { id: nextId || null, name: nextName || null },
    }));
  } catch { /* ignore */ }
}

export function LocationScopeProvider({ children }) {
  const [id, setId] = useState(() => localStorage.getItem(KEY) || null);
  const [name, setName] = useState(() => localStorage.getItem(KEY_NAME) || null);

  useEffect(() => {
    if (id) { localStorage.setItem(KEY, id); localStorage.setItem(KEY_NAME, name || ""); }
    else { localStorage.removeItem(KEY); localStorage.removeItem(KEY_NAME); }
  }, [id, name]);

  const setScope = useCallback((nextId, nextName) => {
    setId(nextId);
    setName(nextName || null);
    emitChange(nextId, nextName);
  }, []);
  const clear = useCallback(() => {
    setId(null);
    setName(null);
    emitChange(null, null);
  }, []);
  return (
    <LocationScopeContext.Provider value={{ id, name, setScope, clear }}>
      {children}
    </LocationScopeContext.Provider>
  );
}
export const useLocationScope = () => useContext(LocationScopeContext);

// Read current active scope directly from localStorage (used by axios
// interceptor which lives outside React).
export function getActiveLocationId() {
  try { return localStorage.getItem(KEY) || null; } catch { return null; }
}
